"use client";

import React, { createContext, useCallback, useContext, useEffect, ReactNode, useState } from 'react';

interface TaskContextType {
    activeTaskOrderId: string | null;
    startTracking: (orderId: string) => void;
    stopTracking: () => void;
    isTaskComplete: boolean;
    setIsTaskComplete: (complete: boolean) => void;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export function TaskProvider({ children }: { children: ReactNode }) {
    const [activeTaskOrderId, setActiveTaskOrderId] = useState<string | null>(null);
    const [isTaskComplete, setIsTaskComplete] = useState(false);

    const stopTracking = useCallback(() => {
        setActiveTaskOrderId(null);
        setIsTaskComplete(false);
        if (typeof window !== 'undefined') {
            localStorage.removeItem('activeTaskOrderId');
            localStorage.removeItem('taskStartTime');
        }
    }, []);

    const startTracking = useCallback((orderId: string) => {
        setActiveTaskOrderId(orderId);
        setIsTaskComplete(false);
        if (typeof window !== 'undefined') {
            localStorage.setItem('activeTaskOrderId', orderId);
            localStorage.setItem('taskStartTime', new Date().toISOString());
        }
    }, []);

    // Load from localStorage on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const savedOrderId = localStorage.getItem('activeTaskOrderId');
            if (savedOrderId) {
                setActiveTaskOrderId(savedOrderId);
            }
        }
    }, []);

    // Listen for logout event and clear task tracking data
    useEffect(() => {
        const handleLogout = () => {
            stopTracking();
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('auth:logout', handleLogout);
        }

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('auth:logout', handleLogout);
            }
        };
    }, [stopTracking]);

    return (
        <TaskContext.Provider value={{
            activeTaskOrderId,
            startTracking,
            stopTracking,
            isTaskComplete,
            setIsTaskComplete
        }}>
            {children}
        </TaskContext.Provider>
    );
}

export function useTask() {
    const context = useContext(TaskContext);
    if (context === undefined) {
        throw new Error('useTask must be used within a TaskProvider');
    }
    return context;
}
